import { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CardCharts from "../../components/common/CardCharts/CardCharts";
import CardStats from "../../components/common/CardStats/CardStats";
import ProgressCircle from "../../components/common/ProgressCircle/ProgressCircle";
import MonthlyLineChartGraph from "../../components/common/Charts/MonthlyLineChartGraph";
import DonutChartGraph from "../../components/common/Charts/DonutChartGraph";
import GaugeChart from "../../components/common/Charts/GaugeChart";
import PageLayout from "../../Layouts/PageLayout/PageLayout";
import {
    barChartData,
    lineChartData,
    dashboardStatsData,
    indicadoresData,
} from "./data/data";
import "./Dashboard.scss";
import MapComponent from "../../components/common/MapComponent/MapComponent";
import { useEffect } from "react";
import { getApiInstance } from "../../utils/api";
import { useZone } from "../../context/ZoneContext";
import { useMoneda } from "../../context/MonedaContext";
import { formatDate } from "../../utils/utils";
import { FiPlay, FiUsers } from "react-icons/fi";
import { IoStopOutline } from "react-icons/io5";
import { LuWeight } from "react-icons/lu";
import { MdEuroSymbol } from "react-icons/md";
import ProgressBar from "../../components/common/ProgressBar/ProgressBar";
import Badge from "../../components/common/Badge/Badge";
import Button from "../../components/common/Button/Button";
import { CiClock1 } from "react-icons/ci";
import { CgDanger } from "react-icons/cg";
import { CiCircleCheck } from "react-icons/ci";
import { CiCircleInfo } from "react-icons/ci";
import { CiWarning } from "react-icons/ci";
import PieChartGraph from "../../components/common/Charts/PieChartGraph";
import Modal from "../../components/common/Modal/Modal";
import YearSelect from "../../components/common/YearSelect/YearSelect";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
} from "recharts";

function DashboardSkeleton() {
    return (
        <div className="dashboard-skeleton">
            <div className="dashboard__hero">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="dashboard-skeleton__card">
                        <div className="dashboard-skeleton__card-title" />
                        <div className="dashboard-skeleton__card-subtitle" />
                        <div className="dashboard-skeleton__card-metric" />
                        <div className="dashboard-skeleton__card-chart" />
                    </div>
                ))}
            </div>

            <div className="card-charts">
                <div className="dashboard__charts-map">
                    <div className="dashboard__charts-left">
                        <div className="card-charts__header">
                            <div className="card-charts__info card-charts__info--no-tabs">
                                <div className="card-charts__text">
                                    <div className="dashboard-skeleton__section-title" />
                                    <div className="dashboard-skeleton__section-subtitle" />
                                </div>
                            </div>
                        </div>
                        <div className="dashboard-skeleton__table">
                            <div className="dashboard-skeleton__table-header">
                                <div className="dashboard-skeleton__th" />
                                <div className="dashboard-skeleton__th" />
                                <div className="dashboard-skeleton__th" />
                                <div className="dashboard-skeleton__th" />
                                <div className="dashboard-skeleton__th" />
                            </div>
                            <div className="dashboard-skeleton__table-row dashboard-skeleton__table-row--total">
                                {Array.from({ length: 5 }).map((_, j) => (
                                    <div key={j} className="dashboard-skeleton__td" />
                                ))}
                            </div>
                            {Array.from({ length: 4 }).map((_, j) => (
                                <div key={j} className="dashboard-skeleton__table-row">
                                    {Array.from({ length: 5 }).map((_, k) => (
                                        <div key={k} className="dashboard-skeleton__td" />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="dashboard-skeleton__map" />
                </div>
            </div>
        </div>
    );
}

const cumplimientoStates = [
    { label: "Sin datos", type: "info", Icon: CiClock1 },
    { label: "Pendiente", type: "info", Icon: CiClock1 },
    { label: "Validado con incidencias", type: "warning", Icon: CgDanger },
    { label: "Validada", type: "success", Icon: CiCircleCheck },
];

const periodOptions = [
    { value: "Semana", label: "Semana" },
    { value: "Mes", label: "Mes" },
    { value: "Año", label: "Año" },
];

const COLOR_RP = "#0b6775";
const COLOR_RNP = "#5599a3";

const PAIS_FLAGS = {
    España: "ES",
    Eslovaquia: "SK",
    "Reino Unido": "GB",
    Italia: "IT",
    Bélgica: "BE",
    Polonia: "PL",
    Francia: "FR",
    Portugal: "PT",
    Alemania: "DE",
    Noruega: "NO",
    Holanda: "NL",
    "Países Bajos": "NL",
    México: "MX",
    "Estados Unidos": "US",
    Austria: "AT",
    Suiza: "CH",
    Suecia: "SE",
    Dinamarca: "DK",
    Irlanda: "IE",
    Hungría: "HU",
    "República Checa": "CZ",
    Rumanía: "RO",
    Grecia: "GR",
};

const getFlagEmoji = (pais) => {
    const code = PAIS_FLAGS?.[pais];

    if (!code) return "🏳️";

    return code
        .toUpperCase()
        .replace(/./g, (c) =>
            String.fromCodePoint(127397 + c.charCodeAt(0))
        );
};

const formatNumberEs = (value, decimals = 2) =>
    new Intl.NumberFormat("es-ES", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(value);

/* Coordenadas fijas para el mapa de la demo: da igual lo que traiga EjeX/EjeY
   de la base de datos, aquí siempre caen en España/Europa. [lat, lng]. */
const COORDENADAS_DEMO_POR_OBRA = {
    1: [43.3623, -8.4115], // A Coruña
    2: [40.4168, -3.7038], // Madrid
    3: [41.3874, 2.1734], // Barcelona
    4: [39.4699, -0.3763], // Valencia
    5: [37.3891, -5.9845], // Sevilla
    6: [43.263, -2.9253], // Bilbao
    7: [43.4623, -3.8044], // Santander
    8: [41.6488, -0.8891], // Zaragoza
    9: [39.5696, 2.6502], // Palma de Mallorca
    11: [41.44, 2.25], // Barcelona
    12: [39.51, -0.44], // Valencia
    13: [37.34, -5.91], // Sevilla
    14: [43.29, -2.98], // Bilbao
    15: [43.42, -3.74], // Santander
    16: [41.7, -0.96], // Zaragoza
    17: [39.54, 2.7], // Palma de Mallorca
    19: [37.45, -6.03], // Sevilla
    20: [43.4, -8.47], // A Coruña
};

/* Cualquier obra que no esté en la tabla de arriba (no debería pasar con las
   de la demo) cae igualmente dentro de España, en un punto distinto según su
   CodObra, en vez de arriesgarse a lo que traiga la BD. */
const coordenadaDemo = (codObra) => {
    const fija = COORDENADAS_DEMO_POR_OBRA[Number(codObra)];
    if (fija) return fija;

    const n = Number(codObra) || 0;
    return [40.0 + ((n * 37) % 400) / 100 - 2, -4.0 + ((n * 53) % 600) / 100 - 3];
};

export default function Dashboard() {
    const navigate = useNavigate();
    const [obras, setObras] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { moneda } = useMoneda();

    const { zoneOptions, selectedZoneValues, toggleZone, isZoneSelected } =
        useZone();
    const zonasQuery = selectedZoneValues.join(",");
    const [mapCenter, setMapCenter] = useState([40.0, -4.0]);
    const [zoom, setZoom] = useState(5);
    const [selectedPeriod, setSelectedPeriod] = useState("Total");
    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i).map(
        (year) => ({ label: String(year), value: year })
    );
    const [informeCantidadesGeneradas, setInformeCantidadesGeneradas] =
        useState([]);
    const [informeDashboard, setInformeDashboard] = useState([]);
    const [porcentajeValorizacion, setPorcentajeValorizacion] =
        useState(null);
    const [pesosPorMes, setPesosPorMes] = useState(null);
    const [totalTnGeneradasServer, setTotalTnGeneradasServer] =
        useState(null);
    const [principalesAreas, setPrincipalesAreas] = useState([]);
    const [obrasDashboard, setObrasDashboard] = useState([]);
    const [paisDetalle, setPaisDetalle] = useState(null);

    /* Identifica la petición en curso: si el usuario cambia de zona antes de
       que responda la anterior, la respuesta vieja se descarta. */
    const peticionRef = useRef(0);

    const parseApiNumber = (value, fallback = 0) => {
        if (value === null || value === undefined || value === "") {
            return fallback;
        }

        if (typeof value === "number") {
            return Number.isFinite(value) ? value : fallback;
        }

        if (typeof value === "string") {
            const normalized = value
                .trim()
                .replace(/\./g, "")
                .replace(",", ".");

            const parsed = Number(normalized);

            return Number.isFinite(parsed) ? parsed : fallback;
        }

        const parsed = Number(value);

        return Number.isFinite(parsed) ? parsed : fallback;
    };

    const fetchDashboardData = async () => {
        if (!zonasQuery || !moneda) return;

        const idPeticion = ++peticionRef.current;
        const esVigente = () => idPeticion === peticionRef.current;

        setLoading(true);
        setError(null);

        const api = getApiInstance();
        const zonasPeticion = selectedZoneValues;

        /* Las peticiones salen en paralelo: los KPIs ya no esperan a que
           termine GetObras, que solo alimenta el mapa y es la más pesada. */
        const kpisTask = api
            .get(
                `/api/Informe/GetDashboardChartsNew?anio=${selectedYear}&codZonas=${zonasQuery}&moneda=${moneda}`
            )
            .then((dashboardNewResponse) => {
                if (!esVigente()) return;

                const dashboardNewData = dashboardNewResponse.data ?? {};

                const listaInforme = Array.isArray(dashboardNewData)
                    ? dashboardNewData
                    : Array.isArray(dashboardNewData?.totalCentros)
                      ? dashboardNewData.totalCentros
                      : Array.isArray(dashboardNewData?.data)
                        ? dashboardNewData.data
                        : [];

                setInformeDashboard(listaInforme);
                setPorcentajeValorizacion(
                    dashboardNewData?.porcentajeValorizacion ??
                        dashboardNewData?.PorcentajeValorizacion ??
                        null
                );
                setPesosPorMes(
                    dashboardNewData?.pesosPorMes ??
                        dashboardNewData?.PesosPorMes ??
                        null
                );
                setTotalTnGeneradasServer(
                    dashboardNewData?.totalTnGeneradas ??
                        dashboardNewData?.TotalTnGeneradas ??
                        null
                );
                setPrincipalesAreas(
                    dashboardNewData?.principalesAreas ??
                        dashboardNewData?.PrincipalesAreas ??
                        []
                );
                setObrasDashboard(
                    dashboardNewData?.obras ??
                        dashboardNewData?.Obras ??
                        []
                );
            })
            .catch((err) => {
                if (!esVigente()) return;

                console.warn("GetDashboardChartsNew no disponible:", err);
                setInformeDashboard([]);
                setPorcentajeValorizacion(null);
                setPesosPorMes(null);
                setTotalTnGeneradasServer(null);
                setPrincipalesAreas([]);
                setObrasDashboard([]);
            });

        const obrasTask = api
            .get(`/api/Obra/GetObras/es`)
            .then((obrasResponse) => {
                if (!esVigente()) return;

                const obrasData = (
                    Array.isArray(obrasResponse.data) ? obrasResponse.data : []
                ).filter((obra) =>
                    zonasPeticion.includes(
                        String(obra?.codZona ?? obra?.CodZona ?? "")
                    )
                );

                setObras(obrasData);
            })
            .catch((err) => {
                if (!esVigente()) return;

                console.error(err);
                setError(
                    "No se han podido cargar los datos del dashboard. Vuelve a intentarlo más tarde."
                );
            });

        const cantidadesTask = api
            .get(`/api/Informe/GetCantidadesGeneradas`)
            .then((cantidadesResponse) => {
                if (!esVigente()) return;

                setInformeCantidadesGeneradas(
                    Array.isArray(cantidadesResponse.data)
                        ? cantidadesResponse.data
                        : []
                );
            })
            .catch((err) => {
                console.warn("GetCantidadesGeneradas no disponible:", err);
            });

        /* El indicador de carga se apaga en cuanto responden los KPIs; el mapa
           (GetObras) y las cantidades terminan de cargar en segundo plano. */
        kpisTask.finally(() => {
            if (esVigente()) setLoading(false);
        });

        await Promise.allSettled([kpisTask, obrasTask, cantidadesTask]);
    };

    useEffect(() => {
        if (!zonasQuery || !moneda) return;

        fetchDashboardData();

        // Con una única zona activa centramos el mapa en ella; con varias,
        // vista general.
        const zonaUnica =
            selectedZoneValues.length === 1 ? selectedZoneValues[0] : null;

        if (zonaUnica === "2") {
            setMapCenter([24.056145870396488, -102.51185975545415]);
            setZoom(5);
        } else if (zonaUnica === "3") {
            setMapCenter([32.679109313985286, -28.420501580530633]);
            setZoom(1);
        } else if (zonaUnica === "1") {
            setMapCenter([40.0, -4.0]);
            setZoom(5);
        } else {
            setMapCenter([32.679109313985286, -28.420501580530633]);
            setZoom(1);
        }
    }, [zonasQuery, selectedYear, moneda]);

    const getEstadoObraCategoria = (obra) => {
        const estado = String(obra?.estado ?? "")
            .toUpperCase()
            .trim();
        const cod = String(obra?.codEstado ?? "").trim();

        const norm = (s) =>
            s.toUpperCase().replace(/[_\-\s]+/g, "").trim();

        const valorEstado = norm(estado);
        const valorCod = norm(cod);

        // CLOSEDREQUEST=6 e INCORRECT=7: no se contabilizan en el pie
        if (["6", "7"].includes(valorCod)) return null;

        const isSinDatos =
            estado === "" ||
            ["SINDATOS", "NODATA", "SIN_DATOS", "SIN DATOS"].includes(
                valorEstado
            ) ||
            valorCod === "1";

        const isEnProceso =
            ["ENPROCESO", "EN_PROGRESO", "PENDIENTE", "PROCESO"].includes(
                valorEstado
            ) || valorCod === "3";

        const isIncidencia =
            ["INCIDENCIA", "INCIDENCIAS", "RETRASADO", "RETRASOS"].includes(
                valorEstado
            ) || valorCod === "4";

        const isValidadoOCerrado =
            [
                "VALIDADO",
                "VALIDADA",
                "VALIDADOS",
                "VALIDADAS",
                "CERRADO",
                "CERRADA",
                "CERRADOS",
                "CERRADAS",
                "COMPLETADO",
                "COMPLETADA",
            ].includes(valorEstado) ||
            ["2", "5"].includes(valorCod);

        if (isIncidencia) return "Incidencias";
        if (isEnProceso) return "En proceso";
        if (isValidadoOCerrado) return "Validados/Cerrados";
        return "Sin datos";
    };

    const estadoObraCategorias = [
        "Sin datos",
        "En proceso",
        "Incidencias",
        "Validados/Cerrados",
    ];

    const estadoObraColores = ["#5599a3", "#f97316", "#0b6775", "#9ca3af"];

    const fuenteCentros = Array.isArray(informeDashboard)
        ? informeDashboard
        : [];

    const conteoEstadoObra = useMemo(() => {
        const counts = {
            "Sin datos": 0,
            "En proceso": 0,
            "Incidencias": 0,
            "Validados/Cerrados": 0,
        };

        (fuenteCentros || []).forEach((obra) => {
            const categoria = getEstadoObraCategoria(obra);
            if (categoria === null) return;
            if (counts[categoria] !== undefined) {
                counts[categoria]++;
            } else {
                counts["Sin datos"]++;
            }
        });

        return estadoObraCategorias.map((categoria) => ({
            name: categoria,
            value: counts[categoria],
        }));
    }, [fuenteCentros]);

    const totalCentros = Array.isArray(fuenteCentros)
        ? fuenteCentros.length
        : 0;

    const incidenciasCount = Array.isArray(fuenteCentros)
        ? fuenteCentros.filter(
              (obra) => getEstadoObraCategoria(obra) === "Incidencias"
          ).length
        : 0;

    const correctosCount = Array.isArray(fuenteCentros)
        ? fuenteCentros.filter(
              (obra) => String(obra?.codEstado ?? "").trim() === "2"
          ).length
        : 0;

    const MESES = [
        "Ene",
        "Feb",
        "Mar",
        "Abr",
        "May",
        "Jun",
        "Jul",
        "Ago",
        "Sep",
        "Oct",
        "Nov",
        "Dic",
    ];

    const datosMensualesCantidad = useMemo(() => {
        if (Array.isArray(pesosPorMes) && pesosPorMes.length > 0) {
            return pesosPorMes.map((peso, index) => ({
                name: MESES[index] ?? "",
                value: parseApiNumber(peso, 0),
            }));
        }

        const acumulado = new Array(12).fill(0);

        (informeDashboard || []).forEach((item) => {
            const fecha = item.fechaInicio;
            if (!fecha) return;

            const date = new Date(fecha);
            if (Number.isNaN(+date)) return;

            const year = date.getFullYear();
            if (year > 1950 && year <= new Date().getFullYear()) {
                const mes = date.getMonth();
                acumulado[mes] += parseApiNumber(item.peso, 0);
            }
        });

        return MESES.map((name, index) => ({
            name,
            value: acumulado[index],
        }));
    }, [informeDashboard, pesosPorMes]);

    const totalTnGeneradas = useMemo(() => {
        const serverTotal = parseApiNumber(
            totalTnGeneradasServer,
            Number.NaN
        );

        if (
            totalTnGeneradasServer !== null &&
            totalTnGeneradasServer !== undefined &&
            Number.isFinite(serverTotal)
        ) {
            return serverTotal;
        }

        return (informeDashboard || []).reduce(
            (sum, item) => sum + parseApiNumber(item.peso, 0),
            0
        );
    }, [informeDashboard, totalTnGeneradasServer]);

    /* El eje Y se calcula a partir de los datos: con un eje fijo, las zonas
       con poco volumen dibujaban la línea pegada al suelo y parecía que el
       gráfico no reaccionaba al filtro de zonas. */
    const yTicksCantidad = useMemo(() => {
        const maximo = (datosMensualesCantidad || []).reduce(
            (max, punto) => Math.max(max, parseApiNumber(punto.value, 0)),
            0
        );

        if (maximo <= 0) return [];

        const bruto = maximo / 5;
        const exponente = Math.floor(Math.log10(bruto));
        const base = Math.pow(10, exponente);
        const fraccion = bruto / base;
        const multiplo =
            fraccion <= 1 ? 1 : fraccion <= 2 ? 2 : fraccion <= 5 ? 5 : 10;
        const paso = multiplo * base;

        return Array.from({ length: 5 }, (_, i) => paso * (i + 1));
    }, [datosMensualesCantidad]);

    const formatTn = (value) =>
        new Intl.NumberFormat("es-ES", {
            maximumFractionDigits: 0,
        }).format(value);

    const datosValorizacion = useMemo(() => {
        const serverPorcVal = parseApiNumber(
            porcentajeValorizacion,
            Number.NaN
        );

        if (
            porcentajeValorizacion !== null &&
            porcentajeValorizacion !== undefined &&
            Number.isFinite(serverPorcVal)
        ) {
            const porcVal = Math.max(0, Math.min(100, serverPorcVal));
            return {
                datos: [
                    { name: "Valorización", value: porcVal },
                    { name: "Eliminación", value: 100 - porcVal },
                ],
                porcentajeValorizacion: porcVal,
                porcentajeEliminacion: 100 - porcVal,
            };
        }

        let totalValorizada = 0;
        let totalEliminada = 0;

        const fuente = Array.isArray(informeDashboard)
            ? informeDashboard
            : [];

        (fuente || []).forEach((item) => {
            totalValorizada += parseApiNumber(item.valorizacion, 0);
            totalEliminada += parseApiNumber(item.eliminacion, 0);
        });

        const granTotal = totalValorizada + totalEliminada;
        const porcVal = granTotal > 0 ? (totalValorizada / granTotal) * 100 : 0;
        const porcElim = granTotal > 0 ? (totalEliminada / granTotal) * 100 : 0;

        return {
            datos: [
                { name: "Valorización", value: porcVal },
                { name: "Eliminación", value: porcElim },
            ],
            porcentajeValorizacion: porcVal,
            porcentajeEliminacion: porcElim,
        };
    }, [informeDashboard, porcentajeValorizacion]);

    const datosValorizacionPorPais = useMemo(() => {
        const fuente =
            Array.isArray(principalesAreas) && principalesAreas.length > 0
                ? principalesAreas
                : null;

        const rows = [];

        if (fuente) {
            const porPais = {};

            fuente.forEach((item) => {
                const anioItem = parseApiNumber(
                    item.año ?? item.Año ?? item.anio,
                    Number.NaN
                );

                if (!Number.isFinite(anioItem) || anioItem !== selectedYear) {
                    return;
                }

                const nombre = String(
                    item.pais ?? item.Pais ?? "Sin país"
                ).trim();

                if (!nombre) return;

                if (!porPais[nombre]) {
                    porPais[nombre] = {
                        peso: 0,
                        rp: 0,
                        rnp: 0,
                        sumPorc: 0,
                        countPorc: 0,
                        flag: item.flag ?? "",
                    };
                }

                const p = porPais[nombre];
                p.peso += parseApiNumber(item.peso, 0);
                p.rp += parseApiNumber(item.pesoRP ?? item.pesoRp, 0);
                p.rnp += parseApiNumber(item.pesoRNP ?? item.pesoRnp, 0);

                const porc = parseApiNumber(
                    item.porcentajeValorizacion,
                    Number.NaN
                );
                if (Number.isFinite(porc)) {
                    p.sumPorc += porc;
                    p.countPorc++;
                }
            });

            Object.keys(porPais).forEach((nombre) => {
                const p = porPais[nombre];
                const porcVal =
                    p.countPorc > 0 ? p.sumPorc / p.countPorc : 0;
                rows.push({
                    pais: nombre,
                    flag: p.flag,
                    peso: p.peso,
                    rp: p.rp,
                    rnp: p.rnp,
                    porcVal,
                });
            });
        } else {
            const porPais = {};

            (informeDashboard || []).forEach((obra) => {
                const nombre = String(
                    obra.pais ?? obra.Pais ?? "Sin país"
                ).trim();

                if (!nombre) return;

                if (!porPais[nombre]) {
                    porPais[nombre] = {
                        peso: 0,
                        rp: 0,
                        rnp: 0,
                        valorizado: 0,
                    };
                }

                const item = porPais[nombre];
                item.peso += parseApiNumber(obra.peso, 0);
                item.rp += parseApiNumber(obra.pesoRP, 0);
                item.rnp += parseApiNumber(obra.pesoRNP, 0);
                item.valorizado += parseApiNumber(obra.valorizacion, 0);
            });

            Object.keys(porPais).forEach((nombre) => {
                const item = porPais[nombre];
                const pct =
                    item.peso > 0 ? (item.valorizado / item.peso) * 100 : 0;
                rows.push({
                    pais: nombre,
                    flag: "",
                    peso: item.peso,
                    rp: item.rp,
                    rnp: item.rnp,
                    porcVal: pct,
                });
            });
        }

        rows.sort((a, b) => b.porcVal - a.porcVal);

        const total = rows.reduce(
            (acc, p) => {
                acc.peso += p.peso;
                acc.rp += p.rp;
                acc.rnp += p.rnp;
                return acc;
            },
            { peso: 0, rp: 0, rnp: 0 }
        );
        total.porcVal =
            rows.length > 0
                ? rows.reduce((s, p) => s + p.porcVal, 0) / rows.length
                : 0;

        return { rows, total };
    }, [principalesAreas, informeDashboard, selectedYear]);

    const datosPrincipalesAreas = useMemo(() => {
        return datosValorizacionPorPais.rows.slice(0, 5).map((pais) => ({
            id: pais.pais,
            pais: pais.pais,
            flag: pais.flag,
            cantidadGenerada: formatNumberEs(pais.peso, 2),
            rp: formatNumberEs(pais.rp, 2),
            rnp: formatNumberEs(pais.rnp, 2),
            valorizacion: `${formatNumberEs(pais.porcVal, 2)}%`,
        }));
    }, [datosValorizacionPorPais]);

    const totalesPrincipalesAreas = useMemo(() => {
        const t = datosValorizacionPorPais.total;

        return {
            pais: "Total",
            cantidadGenerada: formatNumberEs(t.peso),
            rp: formatNumberEs(t.rp),
            rnp: formatNumberEs(t.rnp),
            valorizacion: `${formatNumberEs(t.porcVal)}%`,
        };
    }, [datosValorizacionPorPais]);

    const anioActual = new Date().getFullYear();

    const getPaisDeCentro = (obra) => {
        const valor =
            obra?.pais ??
            obra?.Pais ??
            obra?.nombrePais ??
            obra?.paisNombre ??
            "";

        return typeof valor === "string" ? valor.trim().toLowerCase() : "";
    };

    const buildConteoEstado = (centros) => {
        const counts = {
            "Sin datos": 0,
            "En proceso": 0,
            "Incidencias": 0,
            "Validados/Cerrados": 0,
        };

        (centros || []).forEach((obra) => {
            const cod = String(obra?.codEstado ?? "").trim();

            if (cod === "1") counts["Sin datos"]++;
            else if (cod === "3") counts["En proceso"]++;
            else if (cod === "4") counts["Incidencias"]++;
            else counts["Validados/Cerrados"]++;
        });

        return estadoObraCategorias.map((categoria) => ({
            name: categoria,
            value: counts[categoria],
        }));
    };

    const centrosPaisDetalle = useMemo(() => {
        if (!paisDetalle) return [];

        const nombre = String(paisDetalle).trim().toLowerCase();

        const codPais = (() => {
            const coincidencia = (principalesAreas || []).find(
                (item) =>
                    String(item.pais ?? item.Pais ?? "")
                        .trim()
                        .toLowerCase() === nombre
            );

            const cod = coincidencia?.codPais ?? coincidencia?.CodPais;
            return cod === null || cod === undefined ? "" : String(cod).trim();
        })();

        const fuente =
            Array.isArray(obrasDashboard) && obrasDashboard.length > 0
                ? obrasDashboard
                : Array.isArray(obras) && obras.length > 0
                  ? obras
                  : informeDashboard;

        return (fuente || []).filter((obra) => {
            const matchPais = getPaisDeCentro(obra) === nombre;

            if (matchPais) return true;

            const cod = String(obra?.codPais ?? obra?.CodPais ?? "").trim();
            return cod !== "" && cod === codPais;
        });
    }, [obrasDashboard, obras, informeDashboard, principalesAreas, paisDetalle]);

    const centrosPaisDetalleAnio = useMemo(() => {
        if (!paisDetalle) return [];

        return centrosPaisDetalle.filter((obra) => {
            if (!obra?.fechaInicio) return false;

            const anio = new Date(obra.fechaInicio).getFullYear();
            return !Number.isNaN(anio) && anio === selectedYear;
        });
    }, [centrosPaisDetalle, selectedYear, paisDetalle]);

    const conteoEstadoPaisDetalle = useMemo(
        () => buildConteoEstado(centrosPaisDetalle),
        [centrosPaisDetalle]
    );

    const conteoEstadoPaisDetalleAnio = useMemo(
        () => buildConteoEstado(centrosPaisDetalleAnio),
        [centrosPaisDetalleAnio]
    );

    const aniosResiduos = useMemo(() => {
        return [anioActual - 2, anioActual - 1, anioActual];
    }, [anioActual]);

    const esResiduoPeligroso = (residuo) => {
        const desc = (
            residuo?.tipoResiduo?.descripcion ??
            residuo?.tipoResiduo ??
            residuo?.descripcionTipo ??
            ""
        )
            .toString()
            .toLowerCase();

        if (desc) {
            return (
                desc.includes("peligroso") && !desc.includes("no peligroso")
            );
        }

        return false;
    };

    const getAnioResiduo = (residuo, obra) => {
        if (residuo?.fecha) {
            const y = new Date(residuo.fecha).getFullYear();
            if (!Number.isNaN(y) && y > 1900) return y;
        }

        if (obra?.fechaInicio) {
            const y = new Date(obra.fechaInicio).getFullYear();
            if (!Number.isNaN(y) && y > 1900) return y;
        }

        return null;
    };

    const datosResiduosPais = useMemo(() => {
        const paisNombre = String(paisDetalle ?? "").trim().toLowerCase();

        const fuentePais = (
            Array.isArray(principalesAreas) ? principalesAreas : []
        ).filter(
            (item) =>
                String(item.pais ?? item.Pais ?? "")
                    .trim()
                    .toLowerCase() === paisNombre
        );

        if (fuentePais.length > 0) {
            const byYear = {};
            let rpTotal = 0;
            let rnpTotal = 0;

            fuentePais.forEach((item) => {
                const rp = parseApiNumber(item.pesoRP ?? item.pesoRp, 0);
                const rnp = parseApiNumber(
                    item.pesoRNP ?? item.pesoRnp,
                    0
                );
                const porc = parseApiNumber(
                    item.porcentajeValorizacion,
                    Number.NaN
                );

                rpTotal += rp;
                rnpTotal += rnp;

                const anio = parseApiNumber(
                    item.año ?? item.Año ?? item.anio,
                    Number.NaN
                );

                if (Number.isFinite(anio)) {
                    if (!byYear[anio]) {
                        byYear[anio] = { rp: 0, rnp: 0, sumVal: 0, cntVal: 0 };
                    }
                    byYear[anio].rp += rp;
                    byYear[anio].rnp += rnp;

                    if (Number.isFinite(porc)) {
                        byYear[anio].sumVal += porc;
                        byYear[anio].cntVal++;
                    }
                }
            });

            const porAnio = Object.keys(byYear)
                .sort((a, b) => Number(a) - Number(b))
                .map((anio) => ({
                    anio: Number(anio),
                    rp: byYear[anio].rp,
                    rnp: byYear[anio].rnp,
                }));

            const valorizacionPct =
                porAnio.length > 0
                    ? Object.keys(byYear).reduce((suma, anio) => {
                          const y = byYear[anio];
                          return (
                              suma +
                              (y.cntVal > 0 ? y.sumVal / y.cntVal : 0)
                          );
                      }, 0) / porAnio.length
                    : 0;

            const total = rpTotal + rnpTotal;

            return {
                rpTotal,
                rnpTotal,
                total,
                valorizacionPct,
                valorizadoTn: 0,
                porAnio,
            };
        }

        const porAnio = aniosResiduos.map((anio) => ({
            anio,
            rp: 0,
            rnp: 0,
        }));

        let rpTotal = 0;
        let rnpTotal = 0;
        let pesoConDato = 0;
        let pesoValorizado = 0;

        (centrosPaisDetalle || []).forEach((obra) => {
            const residuos = Array.isArray(obra.residuos)
                ? obra.residuos
                : [];

            residuos.forEach((residuo) => {
                const peso = parseApiNumber(
                    residuo.peso ?? residuo.cantidad,
                    0
                );
                const anio = getAnioResiduo(residuo, obra);
                const esRP = esResiduoPeligroso(residuo);

                const par = porAnio.find((p) => p.anio === anio);
                if (par) {
                    if (esRP) par.rp += peso;
                    else par.rnp += peso;
                }

                if (esRP) rpTotal += peso;
                else rnpTotal += peso;

                const valRaw =
                    residuo.valorizacion ??
                    residuo.porcentaje2 ??
                    residuo.porcentaje1;
                const hasDato =
                    valRaw !== null &&
                    valRaw !== undefined &&
                    valRaw !== "";

                if (hasDato) {
                    pesoValorizado += peso * parseApiNumber(valRaw, 0);
                    pesoConDato += peso;
                }
            });
        });

        const total = rpTotal + rnpTotal;
        const valorizacionPct =
            pesoConDato > 0 ? pesoValorizado / pesoConDato : 0;
        const valorizadoTn = pesoConDato > 0 ? pesoValorizado / 100 : 0;

        return {
            rpTotal,
            rnpTotal,
            total,
            valorizacionPct,
            valorizadoTn,
            porAnio,
        };
    }, [
        principalesAreas,
        centrosPaisDetalle,
        paisDetalle,
        aniosResiduos,
    ]);

    const datosChartResiduos = useMemo(() => {
        return datosResiduosPais.porAnio.map((p) => ({
            name: String(p.anio),
            RP: p.rp,
            RNP: p.rnp,
        }));
    }, [datosResiduosPais]);

    const yMaxResiduos = useMemo(() => {
        let max = 0;

        (datosChartResiduos || []).forEach((p) => {
            max = Math.max(
                max,
                parseApiNumber(p.RP, 0),
                parseApiNumber(p.RNP, 0)
            );
        });

        return Math.max(100, Math.ceil((max * 1.15) / 300) * 300);
    }, [datosChartResiduos]);

    const yTicksResiduos = useMemo(() => {
        const ticks = [];
        for (let t = 0; t <= yMaxResiduos; t += 300) ticks.push(t);
        return ticks;
    }, [yMaxResiduos]);

    const renderResiduosStat = (label, value, pct, dotColor) => (
        <div className="dashboard__residuos-stat-row">
            {dotColor && (
                <span
                    className="dashboard__residuos-stat-dot"
                    style={{ background: dotColor }}
                />
            )}
            <span className="dashboard__residuos-stat-label">{label}</span>
            <span className="dashboard__residuos-stat-values">
                <span className="dashboard__residuos-stat-value">
                    {value}
                </span>
                {pct !== null &&
                    pct !== undefined &&
                    pct !== "" && (
                        <span className="dashboard__residuos-stat-pct">
                            ({pct}%)
                        </span>
                    )}
            </span>
        </div>
    );

    const renderPaisDonut = (datos, total) => {
        if (!total) {
            return (
                <div className="dashboard__pais-detalle-vacio">
                    Sin datos para este país
                </div>
            );
        }

        return (
            <PieChart width={220} height={220}>
                <Pie
                    data={datos}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={70}
                    paddingAngle={5}
                    dataKey="value"
                    nameKey="name"
                    label={false}
                >
                    {datos.map((entry, index) => (
                        <Cell
                            key={`cell-${index}`}
                            fill={
                                estadoObraColores[
                                    index % estadoObraColores.length
                                ]
                            }
                        />
                    ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
            </PieChart>
        );
    };

    const renderLegendCentros = (datos, total) => (
        <div className="dashboard__pais-legend">
            <div className="dashboard__pais-legend-total">Total: {total}</div>
            {estadoObraCategorias.map((categoria, index) => {
                const item = datos.find((d) => d.name === categoria);

                return (
                    <div
                        key={categoria}
                        className="dashboard__pais-legend-item"
                    >
                        <span
                            className="dashboard__pais-legend-dot"
                            style={{
                                background:
                                    estadoObraColores[
                                        index % estadoObraColores.length
                                    ],
                            }}
                        />
                        <span className="dashboard__pais-legend-label">
                            {categoria}
                        </span>
                        <span className="dashboard__pais-legend-count">
                            {item?.value ?? 0}
                        </span>
                    </div>
                );
            })}
        </div>
    );

    const getValorizacionStatus = (centro) => {
        // Si viene el porcentaje real de la API, usarlo
        if (
            centro.porcentajeValorizacion !== undefined &&
            centro.porcentajeValorizacion !== null
        ) {
            return Math.max(0, Math.min(100, centro.porcentajeValorizacion));
        }

        // Generar porcentaje único basado en el ID del centro
        const id = centro.codObra || centro.id || centro.codEspacio || "0";
        let hash = 0;
        const str = id.toString();

        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash; // Convert to 32bit integer
        }

        // Convertir hash a porcentaje entre 0-100
        const percentage = Math.abs(hash) % 101;
        return percentage;
    };

    const getCumplimientoStatus = (centro) => {
        switch (centro.codEstado) {
            case "COMPLETADO":
            case "VALIDADO":
                return {
                    status: "CORRECTO",
                    variant: "success",
                    Icon: CiCircleCheck,
                };
            case "INCIDENCIA":
            case "RETRASADO":
                return {
                    status: "INCORRECTO",
                    variant: "danger",
                    Icon: CgDanger,
                };
            case "PENDIENTE":
            case "EN_PROGRESO":
                return {
                    status: "PENDIENTE",
                    variant: "warning",
                    Icon: CiWarning,
                };
            default:
                return {
                    status: "SIN DATOS",
                    variant: "outlined",
                    Icon: CiCircleInfo,
                };
        }
    };

    const modalMap = (centro) => {
        return (
            <div className="centro-map-details">
                <div className="centro-header">
                    <h3 className="centro-title">
                        {centro.nombre || centro.codEspacio || ""},{" "}
                        {centro.direccion || ""}
                    </h3>
                </div>

                <div className="centro-info-section">
                    <div className="info-row">
                        <FiPlay className="info-icon" />
                        <span className="info-label">Fecha inicio</span>
                        <span className="info-value">
                            {formatDate(centro.fechaInicio) || ""}
                        </span>
                    </div>
                    <div className="info-row">
                        <IoStopOutline className="info-icon" />
                        <span className="info-label">Fecha fin</span>
                        <span className="info-value">
                            {formatDate(centro.fechaFin) || ""}
                        </span>
                    </div>

                    <div className="info-row info-row--highlight">
                        <FiUsers className="info-icon" />
                        <span className="info-label">Intermediario</span>
                        <span className="info-value intermediario-link">
                            {centro.intermediario || ""}
                        </span>
                    </div>

                    <div className="info-row">
                        <LuWeight className="info-icon" />
                        <span className="info-label">Cantidades generadas</span>
                        <span className="info-value">{centro.peso || ""}</span>
                    </div>

                    <div className="info-row">
                        <MdEuroSymbol className="info-icon" />
                        <span className="info-label">Coste</span>
                        <span className="info-value">
                            {centro.costeTotal || ""}
                        </span>
                    </div>
                </div>

                <div className="centro-valorizacion-section">
                    <h4 className="section-title">Valorización</h4>
                    <div className="valorizacion-percentage">
                        {(centro.peso
                            ? (centro.valorizacion / centro.peso) * 100
                            : 0
                        ).toFixed(2)}
                        %
                    </div>
                    <ProgressBar
                        percentage={
                            (centro.peso
                                ? (centro.valorizacion / centro.peso) * 100
                                : 0
                            ).toFixed(2) || 0
                        }
                        height={8}
                        width="100%"
                    />
                </div>

                <div className="centro-cumplimiento-section">
                    <div className="cumplimiento-header">
                        <h4 className="section-title">Cumplimiento Legal</h4>
                        <Badge
                            variant={getCumplimientoStatus(centro).variant}
                            Icon={getCumplimientoStatus(centro).Icon}
                        >
                            {getCumplimientoStatus(centro).status}
                        </Badge>
                        {centro.cumplimientos?.map((cumplimiento, index) => (
                            <div key={index} className="cumplimiento-item">
                                <span className="cumplimiento-name">
                                    {cumplimiento.nombre || ""}
                                </span>
                                <Badge
                                    variant={
                                        cumplimientoStates[centro.codEstado]
                                            ?.variant
                                    }
                                    Icon={
                                        cumplimientoStates[centro.codEstado]
                                            ?.Icon
                                    }
                                >
                                    {
                                        cumplimientoStates[centro.codEstado]
                                            ?.status
                                    }
                                </Badge>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="modal-actions">
                    <Button
                        variant="filled"
                        onClick={() => {
                            navigate(`/centros-productores/${centro.codObra}`);
                        }}
                    >
                        Ver Ficha Completa
                    </Button>
                </div>
            </div>
        );
    };

    const currentPeriodOption = periodOptions.find(
        (opt) => opt.value === selectedPeriod
    );

    function buildDashboardData(obras, now = new Date()) {
        // ----------------- Helpers -----------------
        const fmtNumber = (n, decimals = 0) =>
            n.toLocaleString("es-ES", {
                maximumFractionDigits: decimals,
                minimumFractionDigits: decimals,
            });

        const fmtTn = (tn, decimals = 3) => `${fmtNumber(tn, decimals)} tn`;
        const kgToTn = (kg) => kg / 1000;

        const periodRange = (key, date = new Date()) => {
            const start = new Date(date);
            const end = new Date(date);

            if (key === "Semana") {
                // Lunes a domingo de la semana actual
                const day = (date.getDay() + 6) % 7; // 0 = lunes
                start.setDate(date.getDate() - day);
                start.setHours(0, 0, 0, 0);
                end.setDate(start.getDate() + 6);
                end.setHours(23, 59, 59, 999);
            } else if (key === "Mes") {
                start.setDate(1);
                start.setHours(0, 0, 0, 0);
                end.setMonth(start.getMonth() + 1, 0);
                end.setHours(23, 59, 59, 999);
            } else {
                start.setMonth(0, 1);
                start.setHours(0, 0, 0, 0);
                end.setMonth(11, 31);
                end.setHours(23, 59, 59, 999);
            }
            return { start, end };
        };

        const previousPeriodRange = (key, date = new Date()) => {
            const { start } = periodRange(key, date);
            const prevEnd = new Date(start.getTime() - 1);
            if (key === "Semana") {
                const prevStart = new Date(prevEnd);
                prevStart.setDate(prevEnd.getDate() - 6);
                prevStart.setHours(0, 0, 0, 0);
                prevEnd.setHours(23, 59, 59, 999);
                return { start: prevStart, end: prevEnd };
            } else if (key === "Mes") {
                const prevStart = new Date(start);
                prevStart.setMonth(start.getMonth() - 1, 1);
                prevStart.setHours(0, 0, 0, 0);
                const prevEnd2 = new Date(prevStart);
                prevEnd2.setMonth(prevStart.getMonth() + 1, 0);
                prevEnd2.setHours(23, 59, 59, 999);
                return { start: prevStart, end: prevEnd2 };
            } else {
                const prevStart = new Date(start);
                prevStart.setFullYear(start.getFullYear() - 1, 0, 1);
                prevStart.setHours(0, 0, 0, 0);
                const prevEnd2 = new Date(prevStart);
                prevEnd2.setMonth(11, 31);
                prevEnd2.setHours(23, 59, 59, 999);
                return { start: prevStart, end: prevEnd2 };
            }
        };

        const inRange = (d, start, end) => d >= start && d <= end;

        // Reglas de "activo": ajusta si tienes catálogo de estados cerrados.
        const isActive = (obra) => {
            const estadosNoActivos = new Set([]); // e.g., new Set([9, 10])
            if (obra.codEstado != null && estadosNoActivos.has(obra.codEstado))
                return false;
            if (obra.fechaFin) {
                const fin = new Date(obra.fechaFin);
                if (!Number.isNaN(+fin) && fin < new Date()) return false;
            }
            return true;
        };

        // ---- Total Centros ----
        const computeTotalCentros = (obras) => {
            const activos = obras.filter(isActive).length;
            const metric = {
                amount: String(activos),
                percentageValue: 0,
                percentageText: "sin variación",
            };
            return {
                title: "Total Centros",
                subtitle: "Activos en la red",
                metrics: {
                    Semana: { ...metric },
                    Mes: { ...metric },
                    Año: { ...metric },
                },
            };
        };

        // ---- Cantidad generada (tn) ----
        const sumResiduosInPeriodTn = (obras, start, end) => {
            let totalTn = 0;
            for (const obra of obras) {
                const residuos = Array.isArray(obra.residuos)
                    ? obra.residuos
                    : [];
                for (const r of residuos) {
                    // Si hay fecha, filtra por periodo; si no, puedes incluirlos siempre (aquí: solo si pasan el filtro o no tienen fecha)
                    if (r.fecha) {
                        const d = new Date(r.fecha);
                        if (Number.isNaN(+d) || !inRange(d, start, end))
                            continue;
                    }
                    const qty = typeof r.cantidad === "number" ? r.cantidad : 0;
                    const tn = r.unidad === "kg" ? kgToTn(qty) : qty;
                    totalTn += tn;
                }
            }
            return totalTn;
        };

        const computeCantidadGenerada = (obras, now) => {
            const build = (key) => {
                const { start, end } = periodRange(key, now);
                const { start: ps, end: pe } = previousPeriodRange(key, now);

                const actual = sumResiduosInPeriodTn(obras, start, end);
                const previo = sumResiduosInPeriodTn(obras, ps, pe);
                const pct = previo > 0 ? ((actual - previo) / previo) * 100 : 0;

                return {
                    amount: fmtTn(actual),
                    percentageValue: Number(pct.toFixed(2)),
                    percentageText:
                        previo > 0
                            ? `vs ${key.toLowerCase()} anterior`
                            : "sin variación",
                };
            };

            return {
                title: "Cantidad generada",
                subtitle: "Total procesado",
                metrics: {
                    Semana: build("Semana"),
                    Mes: build("Mes"),
                    Año: build("Año"),
                },
            };
        };

        // ---- Eficiencia global ----
        const sumValorizacionEliminacionInPeriod = (obras, start, end) => {
            let valorizado = 0;
            let eliminado = 0;

            for (const obra of obras) {
                const residuos = Array.isArray(obra.residuos)
                    ? obra.residuos
                    : [];
                let hasResidueLevel = false;

                for (const r of residuos) {
                    if (
                        r.fecha &&
                        (typeof r.valorizado === "number" ||
                            typeof r.eliminado === "number")
                    ) {
                        const d = new Date(r.fecha);
                        if (Number.isNaN(+d) || !inRange(d, start, end))
                            continue;
                        valorizado +=
                            typeof r.valorizado === "number" ? r.valorizado : 0;
                        eliminado +=
                            typeof r.eliminado === "number" ? r.eliminado : 0;
                        hasResidueLevel = true;
                    }
                }

                // Si no hay detalle con fecha, usa totales de obra (sin rango)
                if (!hasResidueLevel) {
                    valorizado +=
                        typeof obra.valorizacion === "number"
                            ? obra.valorizacion
                            : 0;
                    eliminado +=
                        typeof obra.eliminacion === "number"
                            ? obra.eliminacion
                            : 0;
                }
            }

            return { valorizado, eliminado };
        };

        const computeEficienciaGlobal = (obras, now) => {
            const build = (key) => {
                const { start, end } = periodRange(key, now);
                const { start: ps, end: pe } = previousPeriodRange(key, now);

                const { valorizado, eliminado } =
                    sumValorizacionEliminacionInPeriod(obras, start, end);
                const { valorizado: vPrev, eliminado: ePrev } =
                    sumValorizacionEliminacionInPeriod(obras, ps, pe);

                const denom = valorizado + eliminado;
                const denomPrev = vPrev + ePrev;

                const eff = denom > 0 ? valorizado / denom : 0;
                const effPrev = denomPrev > 0 ? vPrev / denomPrev : null;

                const pctDelta = effPrev != null ? (eff - effPrev) * 100 : 0;

                // Formateo de porcentaje "72,21%"
                const amount =
                    (eff * 100).toLocaleString("es-ES", {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                    }) + "%";

                return {
                    amount,
                    percentageValue: Number(pctDelta.toFixed(2)),
                    percentageText:
                        effPrev != null ? "mejora" : "sin variación",
                };
            };

            return {
                title: "Eficiencia global",
                subtitle: "Valorización promedio",
                metrics: {
                    Semana: build("Semana"),
                    Mes: build("Mes"),
                    Año: build("Año"),
                },
            };
        };

        // ----------------- Compose -----------------
        return {
            totalCentros: computeTotalCentros(obras),
            cantidadGenerada: computeCantidadGenerada(obras, now),
            eficienciaGlobal: computeEficienciaGlobal(obras, now),
        };
    }

    const dashboardStatsData = useMemo(() => {
        try {
            if (!Array.isArray(obras)) return null;
            return buildDashboardData(obras, new Date());
        } catch {
            return null;
        }
    }, [obras]);

    const showSkeleton = loading && !error && obras.length === 0;

    return (
        <PageLayout
            title="Resumen de tu actividad"
            subtitle="Aquí visualizarás un resumen de los datos más importantes de tu organización"
            actions={
                <>
                    <div
                        className="dashboard__zonas"
                        role="group"
                        aria-label="Filtrar por zona"
                    >
                        <span className="dashboard__zonas-label">Zonas</span>
                        <div className="dashboard__zonas-pills">
                            {zoneOptions.map((zona) => {
                                const activa = isZoneSelected(zona.value);

                                return (
                                    <button
                                        key={zona.value}
                                        type="button"
                                        aria-pressed={activa}
                                        className={`dashboard__zona-pill ${
                                            activa
                                                ? "dashboard__zona-pill--active"
                                                : ""
                                        }`}
                                        onClick={() => toggleZone(zona.value)}
                                    >
                                        <span
                                            className="dashboard__zona-pill-indicator"
                                            aria-hidden="true"
                                        />
                                        {zona.label}
                                    </button>
                                );
                            })}
                        </div>

                        {loading && (
                            <span
                                className="dashboard__zonas-estado"
                                role="status"
                            >
                                Actualizando…
                            </span>
                        )}
                    </div>

                    <YearSelect
                        options={yearOptions}
                        value={selectedYear}
                        onChange={setSelectedYear}
                    />
                </>
            }
        >
            {showSkeleton && <DashboardSkeleton />}

            {!showSkeleton && (
                <>
                <div
                    className={`dashboard__contenido ${
                        loading ? "dashboard__contenido--actualizando" : ""
                    }`}
                >
                    <div className="dashboard__hero">
                        {error && (
                            <div className="dashboard__banner dashboard__banner--error">
                                {error}
                            </div>
                        )}
                <CardStats
                    title="Total Centros"
                    subtitle={`Año ${selectedYear}`}
                    data={{
                        metrics: {
                            Semana: { amount: String(totalCentros) },
                            Mes: { amount: String(totalCentros) },
                            Año: { amount: String(totalCentros) },
                        },
                        chartData: conteoEstadoObra,
                    }}
                    showTabs={false}
                    showTrend={false}
                    percentage={false}
                    hasChart={true}
                >
                    <PieChartGraph
                        data={conteoEstadoObra}
                        colors={estadoObraColores}
                        includeZero
                        showLabels={false}
                        tooltipAsPercent={false}
                        height={240}
                        innerRadius={45}
                        outerRadius={70}
                    />
                </CardStats>

                <CardStats
                    title="Cantidad generada tn"
                    subtitle={`Año ${selectedYear}`}
                    data={{
                        metrics: {
                            Semana: { amount: `${formatTn(totalTnGeneradas)} tn` },
                            Mes: { amount: `${formatTn(totalTnGeneradas)} tn` },
                            Año: { amount: `${formatTn(totalTnGeneradas)} tn` },
                        },
                        chartData: datosMensualesCantidad,
                    }}
                    showTabs={false}
                    showTrend={false}
                    percentage={false}
                    hasChart={true}
                >
                    <MonthlyLineChartGraph
                        xTicks={["Ene", "Mar", "May", "Jul", "Sep", "Nov"]}
                        yTicks={yTicksCantidad}
                        yFormatter={formatTn}
                        color="#0b6775"
                        height={240}
                    />
                </CardStats>

                <CardStats
                    title="Porcentaje valorización"
                    subtitle={`Año ${selectedYear}`}
                    data={{
                        metrics: {
                            Semana: {
                                amount: `${datosValorizacion.porcentajeValorizacion.toLocaleString(
                                    "es-ES",
                                    { maximumFractionDigits: 2 }
                                )}%`,
                            },
                            Mes: {
                                amount: `${datosValorizacion.porcentajeValorizacion.toLocaleString(
                                    "es-ES",
                                    { maximumFractionDigits: 2 }
                                )}%`,
                            },
                            Año: {
                                amount: `${datosValorizacion.porcentajeValorizacion.toLocaleString(
                                    "es-ES",
                                    { maximumFractionDigits: 2 }
                                )}%`,
                            },
                        },
                        chartData: datosValorizacion.datos,
                    }}
                    showTabs={false}
                    showTrend={false}
                    percentage={false}
                    hasChart={true}
                >
                    <DonutChartGraph
                        colors={["#0b6775", "#5599a3"]}
                        centerValue={`${datosValorizacion.porcentajeValorizacion.toLocaleString(
                            "es-ES",
                            { maximumFractionDigits: 2 }
                        )}%`}
                        centerLabel="Valorización"
                        height={240}
                        innerRadius={50}
                        outerRadius={75}
                    />
                </CardStats>

                <CardStats
                    title="Incidencias"
                    subtitle={`Año ${selectedYear}`}
                    data={{
                        metrics: {
                            Semana: { amount: String(incidenciasCount) },
                            Mes: { amount: String(incidenciasCount) },
                            Año: { amount: String(incidenciasCount) },
                        },
                        chartData: [
                            { name: "Sin incidencias", value: correctosCount },
                            { name: "Incidencias", value: incidenciasCount },
                        ],
                    }}
                    showTabs={false}
                    showTrend={false}
                    percentage={false}
                    hasChart={true}
                >
                    <GaugeChart
                        segments={[
                            {
                                name: "Correctos",
                                value: correctosCount,
                                color: "#5599a3",
                            },
                            {
                                name: "Incidencias",
                                value: incidenciasCount,
                                color: "#0b6775",
                            },
                        ]}
                        total={correctosCount + incidenciasCount}
                        centerValue={`${incidenciasCount} incidencias`}
                        centerLabel={`${correctosCount} Correctos`}
                        height={240}
                    />
                </CardStats>
            </div>

            {/* <div className="dashboard__hero">
                <CardStats
                    title={dashboardStatsData.totalCentros.title}
                    subtitle={dashboardStatsData.totalCentros.subtitle}
                    data={{ metrics: dashboardStatsData.totalCentros.metrics }}
                    showTrend={true}
                    showTabs={false}
                    activePeriod={selectedPeriod}
                />

                <CardStats
                    title={dashboardStatsData.cantidadGenerada.title}
                    subtitle={dashboardStatsData.cantidadGenerada.subtitle}
                    data={{
                        metrics: dashboardStatsData.cantidadGenerada.metrics,
                    }}
                    showTrend={true}
                    showTabs={false}
                    activePeriod={selectedPeriod}
                />

                <CardStats
                    title={dashboardStatsData.eficienciaGlobal.title}
                    subtitle={dashboardStatsData.eficienciaGlobal.subtitle}
                    data={{
                        metrics: dashboardStatsData.eficienciaGlobal.metrics,
                    }}
                    showTrend={true}
                    showTabs={false}
                    activePeriod={selectedPeriod}
                />

                <CardStats
                    title="Coste Total"
                    subtitle="Gastos en gestión"
                    data={{
                        metrics: {
                            Semana: {
                                amount: "45.230€",
                                percentageValue: 8.2,
                                trendDirection: "up",
                            },
                            Mes: {
                                amount: "182.450€",
                                percentageValue: 12.5,
                                trendDirection: "up",
                            },
                            Año: {
                                amount: "2.145.600€",
                                percentageValue: 5.3,
                                trendDirection: "up",
                            },
                        },
                    }}
                    showTrend={true}
                    showTabs={false}
                    activePeriod={selectedPeriod}
                />
            </div> */}

            {/* <div className="dashboard__indicators">
                <CardCharts
                    title="Indicadores clave"
                    subtitle="Métricas de rendimiento"
                    showTabs={false}
                >
                    <div className="dashboard__circles-grid">
                        {indicadoresData[selectedPeriod]?.map(
                            (indicador, index) => (
                                <ProgressCircle
                                    key={index}
                                    percentage={indicador.percentage}
                                    label={indicador.label}
                                    value={indicador.value}
                                    className="progress-circle--large"
                                />
                            )
                        )}
                    </div>
                </CardCharts>
            </div> */}

            <div className="card-charts">
                <div className="dashboard__charts-map">
                    <div className="dashboard__charts-left">
                        <div className="card-charts__header">
                            <div className="card-charts__info card-charts__info--no-tabs">
                                <div className="card-charts__text">
                                    <h3 className="card-charts__title">
                                        Principales áreas por porcentaje de
                                        valorización
                                    </h3>
                                    <p className="card-charts__subtitle">
                                        Dónde el porcentaje de reciclado es
                                        mayor
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="dashboard__charts-chart">
                            <div className="dashboard__top-areas">
                                <table className="dashboard__top-areas-table">
                                    <thead>
                                        <tr>
                                            <th>País</th>
                                            <th>
                                                Cantidades generadas tn
                                            </th>
                                            <th>RP</th>
                                            <th>RNP</th>
                                            <th>Valorización</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="dashboard__top-areas-total">
                                            <td>
                                                <span className="dashboard__top-areas-flag">
                                                    🌍
                                                </span>
                                                Total
                                            </td>
                                            <td>
                                                {totalesPrincipalesAreas.cantidadGenerada}
                                            </td>
                                            <td>
                                                {totalesPrincipalesAreas.rp}
                                            </td>
                                            <td>
                                                {totalesPrincipalesAreas.rnp}
                                            </td>
                                            <td>
                                                {totalesPrincipalesAreas.valorizacion}
                                            </td>
                                        </tr>
                                        {datosPrincipalesAreas.map((pais) => (
                                            <tr
                                                key={pais.pais}
                                                className="dashboard__top-areas-row"
                                                onClick={() =>
                                                    setPaisDetalle(pais.pais)
                                                }
                                            >
                                                <td>
                                                    <span className="dashboard__top-areas-flag">
                                                        {getFlagEmoji(
                                                            pais.pais
                                                        )}
                                                    </span>
                                                    <span className="dashboard__top-areas-pais">
                                                        {pais.pais}
                                                    </span>
                                                </td>
                                                <td>
                                                    {pais.cantidadGenerada}
                                                </td>
                                                <td>{pais.rp}</td>
                                                <td>{pais.rnp}</td>
                                                <td>
                                                    {pais.valorizacion}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <MapComponent
                        data={obras
                            .filter(
                                (obra) =>
                                    String(obra?.codZona ?? obra?.CodZona ?? "") !== "2"
                            )
                            .map((obra) => {
                                const codObra = obra?.codObra ?? obra?.CodObra;
                                const [lat, lng] = coordenadaDemo(codObra);
                                // MapComponent lee la latitud de ejeY y la
                                // longitud de ejeX (así quedó tras el swap).
                                return { ...obra, ejeY: lat, ejeX: lng };
                            })}
                        center={mapCenter}
                        zoom={zoom}
                        height="100%"
                        width="100%"
                        modalContent={modalMap}
                    />
                </div>
            </div>
                </div>

            <Modal
                isOpen={!!paisDetalle}
                onClose={() => setPaisDetalle(null)}
                title={paisDetalle ? `Detalles de ${paisDetalle}` : ""}
                className="dashboard__pais-modal"
            >
                <div className="dashboard__pais-detalle">
                    <div className="dashboard__pais-detalle-grid">
                        <div className="dashboard__pais-detalle-card">
                            <h4 className="dashboard__pais-detalle-title">
                                Centros Productores
                            </h4>
                            <div className="dashboard__pais-detalle-body">
                                <div className="dashboard__pais-detalle-side">
                                    {renderLegendCentros(
                                        conteoEstadoPaisDetalle,
                                        centrosPaisDetalle.length
                                    )}
                                </div>
                                <div className="dashboard__pais-detalle-chart">
                                    {renderPaisDonut(
                                        conteoEstadoPaisDetalle,
                                        centrosPaisDetalle.length
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="dashboard__pais-detalle-card">
                            <h4 className="dashboard__pais-detalle-title">
                                Centros Productores - {selectedYear}
                            </h4>
                            <div className="dashboard__pais-detalle-body">
                                <div className="dashboard__pais-detalle-side">
                                    {renderLegendCentros(
                                        conteoEstadoPaisDetalleAnio,
                                        centrosPaisDetalleAnio.length
                                    )}
                                </div>
                                <div className="dashboard__pais-detalle-chart">
                                    {renderPaisDonut(
                                        conteoEstadoPaisDetalleAnio,
                                        centrosPaisDetalleAnio.length
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="dashboard__pais-detalle-card dashboard__pais-detalle-card--full">
                        <h4 className="dashboard__pais-detalle-title">
                            Todos los residuos
                        </h4>
                        <div className="dashboard__residuos-legend">
                            <div className="dashboard__residuos-legend-item">
                                <span
                                    className="dashboard__residuos-legend-dot"
                                    style={{ background: COLOR_RP }}
                                />
                                RP
                            </div>
                            <div className="dashboard__residuos-legend-item">
                                <span
                                    className="dashboard__residuos-legend-dot"
                                    style={{ background: COLOR_RNP }}
                                />
                                RNP
                            </div>
                        </div>
                        <div className="dashboard__residuos-body">
                            <div className="dashboard__residuos-chart">
                                <BarChart
                                    width={560}
                                    height={240}
                                    data={datosChartResiduos}
                                    margin={{
                                        top: 10,
                                        right: 20,
                                        left: 0,
                                        bottom: 0,
                                    }}
                                >
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        vertical={false}
                                    />
                                    <XAxis dataKey="name" />
                                    <YAxis
                                        domain={[0, yMaxResiduos]}
                                        ticks={yTicksResiduos}
                                    />
                                    <Tooltip
                                        formatter={(value, name) => [
                                        name === "RP"
                                            ? `${formatNumberEs(value * 1000)} kg`
                                            : `${formatNumberEs(value)} tn`,
                                        name,
                                    ]}
                                    />
                                    <Bar
                                        dataKey="RP"
                                        fill={COLOR_RP}
                                        name="RP"
                                    />
                                    <Bar
                                        dataKey="RNP"
                                        fill={COLOR_RNP}
                                        name="RNP"
                                    />
                                </BarChart>
                            </div>
                            <div className="dashboard__residuos-stats">
                                {renderResiduosStat(
                                    "Total",
                                    `${formatNumberEs(
                                        datosResiduosPais.total
                                    )} tn`,
                                    100
                                )}
                                {renderResiduosStat(
                                    "RP",
                                    `${formatNumberEs(
                                        datosResiduosPais.rpTotal * 1000
                                    )} kg`,
                                    datosResiduosPais.total > 0
                                        ? formatNumberEs(
                                              (datosResiduosPais.rpTotal /
                                                  datosResiduosPais.total) *
                                                  100,
                                              1
                                          )
                                        : "0,0",
                                    COLOR_RP
                                )}
                                {renderResiduosStat(
                                    "RNP",
                                    `${formatNumberEs(
                                        datosResiduosPais.rnpTotal
                                    )} tn`,
                                    datosResiduosPais.total > 0
                                        ? formatNumberEs(
                                              (datosResiduosPais.rnpTotal /
                                                  datosResiduosPais.total) *
                                                  100,
                                              1
                                          )
                                        : "0,0",
                                    COLOR_RNP
                                )}
                                {renderResiduosStat(
                                    "Porcentaje de valorización",
                                    `${formatNumberEs(
                                        datosResiduosPais.valorizacionPct,
                                        2
                                    )}%`,
                                    null,
                                    "#0b6775"
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
                </>
            )}
        </PageLayout>
    );
}
